/**
 * Validation for logging an activity (PRD §5.8).
 *
 * Mirrors the API's `createActivitySchema`, so a field is rejected here rather
 * than after a round trip — but the API validates independently, because a
 * client-side schema is a convenience and never the control.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.8
 */
import { z } from 'zod';

/** The seven activity types of PRD §5.8. */
export const ACTIVITY_TYPES = [
  'CALL',
  'MEETING',
  'EMAIL',
  'SITE_VISIT',
  'DEMO',
  'DOCUMENT_RECEIVED',
  'NOTE',
] as const;

export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const activityFormSchema = z.object({
  type: z.enum(ACTIVITY_TYPES),

  /**
   * The date the event happened, as a `<input type="date">` yields it.
   *
   * Converted to an ISO timestamp at midnight UTC. A date input gives
   * `YYYY-MM-DD` with no time, and appending `T00:00:00.000Z` rather than
   * letting `new Date('2026-03-04')` guess keeps the stored instant the same
   * regardless of the logger's timezone — otherwise a note written in Lisbon
   * and one written in São Paulo land on different days.
   */
  occurredOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, 'Choose a date')
    .transform((value) => `${value}T00:00:00.000Z`),

  summary: z.string().trim().min(1, 'Say what happened').max(5_000),
});

export type ActivityFormInput = z.input<typeof activityFormSchema>;
export type ActivityFormOutput = z.output<typeof activityFormSchema>;

/**
 * Today, as a date input expects it.
 *
 * Most entries are logged the day they happen, so this is the default — and
 * `toISOString().slice(0, 10)` is UTC, matching how the value is stored.
 */
export function todayForInput(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** An ISO timestamp back to the date form, for editing an existing entry. */
export function isoToDateInput(iso: string): string {
  return iso.slice(0, 10);
}
